// ============================================================================
// OBT F2-B — Formen görev listesi (my-publications gerçek verisiyle)
// ============================================================================
// Sayaçlar ve kategoriler GERÇEK backend yanıtından türetilir. Sahte veri yok.
// Yeni tasarım sistemi kurulmaz; mevcut shadcn/ui + ObtStates kullanılır.
// ============================================================================

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ClipboardList,
  FileEdit,
  Send,
  Clock,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import {
  ObtPageHeader,
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
  ObtStatTile,
} from '../ObtStates';
import { obtLabel } from '../obtEnums';
import { useMyPublications } from './useMyPublications';
import { TASK_FILTERS } from './responseEnums';

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul', day: '2-digit', month: 'long', year: 'numeric' });
}

function sumField(items, field) {
  return items.reduce((t, p) => t + (typeof p[field] === 'number' ? p[field] : 0), 0);
}

function PublicationCard({ pub, onOpen }) {
  const due = formatDate(pub.response_due_at);
  return (
    <li className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-900">
              {pub.campaign_title || 'Başlıksız bilgi talebi'}
            </p>
            {pub.due_passed && (
              <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-300">
                Süresi geçti
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            {pub.topic ? `Konu: ${pub.topic} · ` : ''}
            {pub.published_by?.name ? `Yayınlayan: ${pub.published_by.name}` : ''}
            {due ? ` · Son cevap: ${due}` : ''}
          </p>
          {pub.description && (
            <p className="mt-1 line-clamp-2 text-xs text-gray-600">{pub.description}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
            <span>{pub.my_park_count ?? 0} park</span>
            <span>{pub.my_draft_count ?? 0} taslak</span>
            <span>{pub.my_submitted_count ?? 0} gönderilmiş</span>
            {(pub.my_revision_count ?? 0) > 0 && (
              <span className="font-medium text-amber-700">
                {pub.my_revision_count} revizyon
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button size="sm" onClick={() => onOpen(pub)}>
            Aç
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </li>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.enabled
 * @param {(pub: object) => void} props.onOpen  Publication detayına geç
 */
export default function ForemanTaskList({ enabled = true, onOpen }) {
  const { loading, error, items, filtered, filter, setFilter, reload } =
    useMyPublications({ enabled });

  // Her sayaç ilgili filtreye tıklanabilir (BM/MY/Müdür panelleriyle tutarlı):
  // karta tıkla → görev listesi o kategoriye süzülür.
  const stats = [
    {
      key: 'total',
      label: 'Bana yöneltilen bilgi talebi',
      value: items.length,
      icon: ClipboardList,
      tone: 'info',
      filterKey: 'all',
    },
    {
      key: 'drafts',
      label: 'Taslak cevabım',
      value: sumField(items, 'my_draft_count'),
      icon: FileEdit,
      tone: 'warning',
      filterKey: 'drafts',
    },
    {
      key: 'submitted',
      label: 'Gönderdiğim cevap',
      value: sumField(items, 'my_submitted_count'),
      icon: Send,
      tone: 'success',
      filterKey: 'submitted',
    },
    {
      key: 'revision',
      label: 'Revizyon istenen',
      value: sumField(items, 'my_revision_count'),
      icon: Clock,
      tone: 'danger',
      filterKey: 'revision',
    },
  ];

  return (
    <div className="space-y-6">
      <ObtPageHeader
        title="Operasyonel Bilgi Toplama"
        description="Size yöneltilen saha bilgi talepleri ve cevap işlemleriniz."
        actions={
          <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Yenile
          </Button>
        }
      />

      {loading && <ObtLoading label="Görevleriniz yükleniyor…" />}
      {!loading && error && <ObtErrorState error={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {stats.map((s) => (
              <ObtStatTile
                key={s.key}
                label={s.label}
                value={s.value}
                icon={s.icon}
                tone={s.tone}
                onClick={s.filterKey ? () => setFilter(s.filterKey) : undefined}
              />
            ))}
          </div>

          {/* Kategori filtreleri (backend sayaçlarından) */}
          <div className="flex flex-wrap gap-2" role="group" aria-label="Görev filtresi">
            {TASK_FILTERS.map((f) => (
              <Button
                key={f.key}
                variant={filter === f.key ? 'default' : 'outline'}
                size="sm"
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Bana Yöneltilen Bilgi Talepleri
                {filter !== 'all' ? ` · ${obtLabel(
                  Object.fromEntries(TASK_FILTERS.map((f) => [f.key, f.label])),
                  filter
                )}` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filtered.length === 0 ? (
                <ObtEmptyState
                  title="Görev bulunmuyor"
                  description="Seçilen filtreyle eşleşen bilgi talebi yok."
                />
              ) : (
                <ul className="space-y-3">
                  {filtered.map((pub) => (
                    <PublicationCard key={pub.publication_id} pub={pub} onOpen={onOpen} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
